import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Copy, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button, Card, Field, Input } from "@/components/kit";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copiado"), () => toast.error("Não foi possível copiar"));
const SEED = { preparando: "Preparando dados de demonstração…", pronto: "Dados de demonstração prontos", erro: "Falha ao preparar os dados" };
export const demoLink = () => `${window.location.origin}/demo`;

function Cred({ label, value, testid }) {
  return (
    <div className="flex items-center gap-2"><span className="caption w-20 shrink-0">{label}</span>
      <code className="flex-1 min-w-0 truncate text-sm" data-testid={testid}>{value}</code>
      <button className="icon-btn" onClick={() => copy(value)} title="Copiar" data-testid={`${testid}-copy`}><Copy size={14} /></button>
    </div>
  );
}

export function NewDemoButton() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [res, setRes] = useState(null);
  const go = useMutation({ mutationFn: () => api("/master/demo-tenants", { method: "POST", body: { name: name.trim() || null } }),
    onSuccess: (r) => { setRes(r); qc.invalidateQueries({ queryKey: ["master-overview"] }); }, onError: (e) => toast.error(e.message) });
  const close = () => { setOpen(false); setRes(null); setName(""); };
  return (
    <>
      <Button variant="secondary" icon={Sparkles} onClick={() => setOpen(true)} data-testid="new-demo-tenant-button">Restaurante demo</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl" data-testid="demo-tenant-dialog">
          <DialogTitle className="h2">{res ? "Restaurante demo criado" : "Novo restaurante demo"}</DialogTitle>
          {!res ? (
            <div className="flex flex-col gap-4">
              <p className="caption">Cria um restaurante com produtos, 5 fichas técnicas com fotos, 3 conferências semanais, tarefas e equipe · pronto para apresentar.</p>
              <Field label="Nome (opcional)" hint="Ex.: nome do cliente que vai ver a apresentação"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Restaurante Demo" data-testid="demo-tenant-name-input" /></Field>
              <Button icon={Sparkles} loading={go.isPending} onClick={() => go.mutate()} data-testid="demo-tenant-create-button">Criar restaurante demo</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3" data-testid="demo-tenant-result">
              <p className="caption">Os dados levam cerca de 1 minuto para ficar prontos. Entre com:</p>
              <Cred label="Endereço" value={res.access_url} testid="demo-result-url" />
              <Cred label="E-mail" value={res.admin_email} testid="demo-result-email" />
              <Cred label="Senha" value={res.admin_password} testid="demo-result-password" />
              <Button variant="secondary" onClick={() => { close(); nav(`/master/tenants/${res.tenant.id}`); }} data-testid="demo-result-open-detail">Ver detalhes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DemoPanel({ d }) {
  const qc = useQueryClient();
  const { data: pf } = useQuery({ queryKey: ["master-platform"], queryFn: () => api("/master/platform") });
  const isPublic = pf?.public_demo_slug === d.slug;
  const pub = useMutation({ mutationFn: (on) => api("/master/public-demo", { method: "PUT", body: { tenant_id: on ? d.id : null } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["master-platform"] }); qc.invalidateQueries({ queryKey: ["platform"] }); toast.success("Demonstração pública atualizada"); }, onError: (e) => toast.error(e.message) });
  return (
    <Card className="flex flex-col gap-3" data-testid="demo-panel">
      <div className="h3 flex items-center gap-2"><Globe size={16} /> Demonstração</div>
      {d.is_demo && (
        <>
          <p className="caption" data-testid="demo-seed-status">{SEED[d.demo_seed] || "Restaurante demo"}</p>
          <Cred label="E-mail" value={d.contact_email} testid="demo-admin-email" />
          {d.demo_admin_password && <Cred label="Senha" value={d.demo_admin_password} testid="demo-admin-password" />}
        </>
      )}
      <p className="caption">{isPublic ? "Este restaurante aparece para visitantes, só para ver, no botão “Ver demonstração” do cadastro." : "Use este restaurante como a demonstração que visitantes podem ver sem login."}</p>
      {isPublic && <Cred label="Link" value={demoLink()} testid="public-demo-link" />}
      {isPublic && <p className="caption">Abra o link em outro navegador ou janela anônima · aqui ele encerraria sua sessão do Painel Master.</p>}
      <Button variant={isPublic ? "ghost" : "secondary"} className="self-start" loading={pub.isPending} onClick={() => pub.mutate(!isPublic)} data-testid="public-demo-toggle">{isPublic ? "Deixar de usar na demonstração" : "Usar na demonstração pública"}</Button>
    </Card>
  );
}
